import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Lightbulb,
  Heart, 
  Target,
  Clock,
  RefreshCw,
  CheckCircle,
  Send,
  X,
  BrainCircuit,
  Sparkles,
  MessageSquare,
  Plus,
  PlayCircle,
  Hammer,
  TestTube,
  Edit3,
  Download,
  FileText,
  UserPlus,
  Smartphone,
  Coffee,
  Home,
  Truck,
  GraduationCap,
  Stethoscope,
  ArrowRight,
  User,
  PlusCircle,
  MessageCircle,
  Eye,
  Settings,
  Zap,
  Star,
  TrendingUp,
  Award,
  ChevronRight,
  ChevronDown,
  Timer,
  BarChart3,
  Layers,
  Globe,
  Menu,
  ArrowLeft
} from 'lucide-react';

// Import components from main site
import HeaderNav from './HeaderNav';
import FooterLan from './FooterLan';

// SEO Component
const SEOHead = () => {
  const title = "Design Thinking Workshop Simulator - Interactive Team Collaboration Tool | Lusidea";
  const description = "Run virtual design thinking workshops with expert teams. Practice the 5-stage process: Empathize, Define, Ideate, Prototype, Test. Get insights for real business challenges.";
  const keywords = "design thinking workshop, design process, innovation workshop, team collaboration, design methodology, business innovation, problem solving workshop";
  
  useEffect(() => {
    // Set document title
    document.title = title;
    
    // Set meta description
    const metaDescription = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    metaDescription.setAttribute('content', description);
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(metaDescription);
    }
    
    // Set meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]') || document.createElement('meta');
    metaKeywords.setAttribute('name', 'keywords');
    metaKeywords.setAttribute('content', keywords);
    if (!document.querySelector('meta[name="keywords"]')) {
      document.head.appendChild(metaKeywords);
    }
    
    // Set Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]') || document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.setAttribute('content', title);
    if (!document.querySelector('meta[property="og:title"]')) {
      document.head.appendChild(ogTitle);
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]') || document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.setAttribute('content', description);
    if (!document.querySelector('meta[property="og:description"]')) {
      document.head.appendChild(ogDescription);
    }
    
    const ogType = document.querySelector('meta[property="og:type"]') || document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.setAttribute('content', 'website');
    if (!document.querySelector('meta[property="og:type"]')) {
      document.head.appendChild(ogType);
    }
    
    // Structured data for SEO
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Course",
      "name": "Design Thinking Workshop Simulator",
      "description": description,
      "provider": {
        "@type": "Organization",
        "name": "Lusidea",
        "url": "https://www.lusidea.com"
      },
      "educationalLevel": "Beginner to Advanced",
      "teaches": [
        "Design Thinking Process",
        "Team Collaboration",
        "Problem Solving",
        "Innovation Methods",
        "User-Centered Design"
      ],
      "courseMode": "online",
      "isAccessibleForFree": true
    };
    
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);
    
    // Cleanup function
    return () => {
      const scripts = document.head.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => {
        if (script.text.includes('"Course"')) {
          document.head.removeChild(script);
        }
      });
    };
  }, []);
  
  return null;
};

// Enhanced workshop topics with cleaner structure
const WORKSHOP_TOPICS = [
  {
    id: 'banking-app',
    title: 'Mobile Banking Experience',
    description: 'Improve digital banking for mobile users',
    icon: <Smartphone className="h-5 w-5 sm:h-6 sm:w-6" />,
    gradient: 'from-blue-500 to-indigo-600',
    category: 'FinTech',
    difficulty: 'Intermediate',
    duration: '45-60 min',
    tags: ['Mobile UX', 'Security', 'Accessibility'],
    challenge: 'Modern users expect banking to be as simple as social media, yet our current app creates friction at every step. Users abandon transactions, struggle with navigation, and express frustration with complex authentication flows. We need to redesign banking for a generation that lives on their phones.',
    metrics: ['User retention +40%', 'Task completion +65%', 'Support calls -30%'],
    personas: [
      { name: 'Maya', role: 'UX Research Lead', avatar: '👩‍💻', bgColor: 'bg-blue-100', perspective: 'User-centered insights', specialty: 'Behavioral analysis' },
      { name: 'Marcus', role: 'Product Strategy', avatar: '👨‍💼', bgColor: 'bg-green-100', perspective: 'Business impact', specialty: 'Growth metrics' },
      { name: 'Priya', role: 'Customer Success', avatar: '👩‍💼', bgColor: 'bg-purple-100', perspective: 'User advocacy', specialty: 'Pain point analysis' }
    ]
  },
  {
    id: 'remote-work',
    title: 'Distributed Team Collaboration',
    description: 'Design better tools for remote teams',
    icon: <Coffee className="h-5 w-5 sm:h-6 sm:w-6" />,
    gradient: 'from-emerald-500 to-green-600',
    category: 'Workplace',
    difficulty: 'Advanced',
    duration: '60-75 min',
    tags: ['Collaboration', 'Productivity', 'Culture'],
    challenge: 'Remote teams are struggling with fragmented communication, decision fatigue, and cultural disconnect. Despite having numerous tools, productivity is declining and team members feel isolated. We need to design a unified experience that makes distributed work feel natural and connected.',
    metrics: ['Team efficiency +50%', 'Meeting effectiveness +35%', 'Employee satisfaction +45%'],
    personas: [
      { name: 'Elena', role: 'Team Operations', avatar: '👩‍💼', bgColor: 'bg-green-100', perspective: 'Workflow optimization', specialty: 'Process design' },
      { name: 'David', role: 'Remote Engineer', avatar: '👨‍💻', bgColor: 'bg-blue-100', perspective: 'Developer experience', specialty: 'Technical workflows' },
      { name: 'Sarah', role: 'People & Culture', avatar: '👩‍🏫', bgColor: 'bg-pink-100', perspective: 'Human connection', specialty: 'Team dynamics' }
    ]
  },
  {
    id: 'smart-home',
    title: 'Accessible Smart Home',
    description: 'Simple home automation for everyone',
    icon: <Home className="h-5 w-5 sm:h-6 sm:w-6" />,
    gradient: 'from-violet-500 to-purple-600',
    category: 'IoT',
    difficulty: 'Expert',
    duration: '75-90 min',
    tags: ['Accessibility', 'IoT', 'Aging in Place'],
    challenge: 'Smart home technology promises independence and safety for aging populations, but current solutions are overwhelming and counterintuitive. Seniors want the benefits without the complexity. How do we create smart homes that feel magical rather than technical?',
    metrics: ['Adoption rate +80%', 'User confidence +70%', 'Family peace of mind +90%'],
    personas: [
      { name: 'Grace', role: 'Inclusive Design', avatar: '👵', bgColor: 'bg-purple-100', perspective: 'Universal accessibility', specialty: 'Age-friendly design' },
      { name: 'Dr. Chen', role: 'Geriatric Specialist', avatar: '👨‍⚕️', bgColor: 'bg-green-100', perspective: 'Health & safety', specialty: 'Clinical insights' },
      { name: 'Alex', role: 'IoT Product Lead', avatar: '👩‍🎨', bgColor: 'bg-orange-100', perspective: 'Technical feasibility', specialty: 'Smart systems' }
    ]
  }
];

// Simplified design thinking stages
const STAGES = [
  { 
    id: 'empathize', 
    name: 'Empathize', 
    icon: Heart, 
    color: 'rose',
    description: 'Understand your users deeply',
    prompt: 'Who are we designing for and what do they truly need?',
    focus: 'User research, interviews, observations, pain points',
    outcomes: ['User personas', 'Journey maps', 'Pain point analysis', 'Emotional insights']
  },
  { 
    id: 'define', 
    name: 'Define', 
    icon: Target, 
    color: 'blue',
    description: 'Frame the right problem to solve',
    prompt: 'What is the core problem we need to solve?',
    focus: 'Problem statements, How Might We questions, constraints',
    outcomes: ['Problem statement', 'Success metrics', 'Design principles', 'Scope definition']
  },
  { 
    id: 'ideate', 
    name: 'Ideate', 
    icon: Lightbulb, 
    color: 'amber',
    description: 'Generate creative solutions',
    prompt: 'How might we solve this problem creatively?',
    focus: 'Brainstorming, creative techniques, solution exploration',
    outcomes: ['Solution concepts', 'Feature ideas', 'Innovation opportunities', 'Design directions']
  },
  { 
    id: 'prototype', 
    name: 'Prototype', 
    icon: Hammer, 
    color: 'green',
    description: 'Build to think and learn',
    prompt: 'What can we build to test our ideas?',
    focus: 'Rapid prototyping, wireframes, mockups, proof of concepts',
    outcomes: ['Interactive prototypes', 'User flows', 'Design specifications', 'Technical requirements']
  },
  { 
    id: 'test', 
    name: 'Test', 
    icon: TestTube, 
    color: 'purple',
    description: 'Validate with real users',
    prompt: 'How will users react to our solution?',
    focus: 'User testing, feedback collection, iteration planning',
    outcomes: ['Test results', 'User feedback', 'Iteration plan', 'Success validation']
  }
];

// Mobile-optimized button component
const Button = ({ children, onClick, isLoading = false, variant = 'primary', icon, className = '', disabled = false, size = 'md', fullWidth = false }) => {
  const variants = {
    primary: `bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white shadow-lg hover:shadow-xl focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    secondary: `bg-purple-600 hover:bg-purple-700 active:bg-purple-800 text-white shadow-lg hover:shadow-xl focus:ring-purple-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    outline: `bg-white border-2 border-blue-300 text-blue-700 hover:bg-blue-50 active:bg-blue-100 hover:border-blue-400 focus:ring-blue-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    ghost: `bg-transparent hover:bg-gray-100 active:bg-gray-200 text-gray-700 hover:text-gray-900 focus:ring-gray-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    success: `bg-green-600 hover:bg-green-700 active:bg-green-800 text-white shadow-lg hover:shadow-xl focus:ring-green-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`,
    danger: `bg-red-600 hover:bg-red-700 active:bg-red-800 text-white shadow-lg hover:shadow-xl focus:ring-red-500 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`
  };
  
  const sizes = {
    sm: 'py-2.5 px-4 text-sm min-h-[44px]',
    md: 'py-3.5 px-6 text-base min-h-[48px]',
    lg: 'py-4 px-8 text-lg min-h-[52px]',
    xl: 'py-5 px-10 text-xl min-h-[56px]'
  };

  return (
    <button
      onClick={onClick}
      disabled={isLoading || disabled}
      className={`${sizes[size]} rounded-xl font-semibold transition-all duration-200 flex items-center justify-center touch-manipulation focus:outline-none focus:ring-2 focus:ring-offset-2 ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      aria-label={typeof children === 'string' ? children : 'Button'}
    >
      {isLoading ? (
        <RefreshCw className="h-5 w-5 animate-spin mr-2 flex-shrink-0" aria-hidden="true" />
      ) : (
        icon && <span className="mr-2 flex-shrink-0" aria-hidden="true">{icon}</span>
      )}
      <span className="truncate">{children}</span>
    </button>
  );
};

// Mobile-optimized toast notification
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => onClose(), 4000);
    return () => clearTimeout(timer);
  }, [onClose]);
  
  const styles = {
    success: 'bg-green-50 text-green-800 border-green-200',
    error: 'bg-red-50 text-red-800 border-red-200',
    info: 'bg-blue-50 text-blue-800 border-blue-200'
  };
  
  return (
    <div 
      className={`fixed top-20 left-4 right-4 sm:top-24 sm:right-4 sm:left-auto z-50 px-4 py-4 rounded-xl shadow-lg border max-w-sm sm:max-w-md flex items-center gap-3 ${styles[type]}`}
      role="alert"
      aria-live="assertive"
    >
      <CheckCircle className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
      <p className="font-medium flex-1 text-sm">{message}</p>
      <button 
        onClick={onClose} 
        className="hover:opacity-75 p-1 min-h-[32px] min-w-[32px] flex items-center justify-center"
        aria-label="Close notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

// Mobile-optimized topic selection card
const TopicCard = ({ topic, onSelect }) => {
  return (
    <article className="group w-full text-left bg-white rounded-2xl border-2 border-gray-200 p-4 sm:p-6 hover:border-blue-300 hover:shadow-lg transition-all duration-200 touch-manipulation min-h-[200px] sm:min-h-[220px]">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${topic.gradient} rounded-xl flex items-center justify-center text-white shadow-lg flex-shrink-0`}>
          {topic.icon}
        </div>
        <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2 py-1 rounded-full ml-2">
          {topic.category}
        </span>
      </div>

      {/* Content */}
      <h3 className="font-bold text-gray-900 text-lg sm:text-xl mb-2 group-hover:text-blue-700 transition-colors leading-tight">{topic.title}</h3>
      <p className="text-gray-600 text-sm mb-4 leading-relaxed line-clamp-2">{topic.description}</p>
      
      {/* Tags - Show fewer on mobile */}
      <div className="flex flex-wrap gap-2 mb-4">
        {topic.tags.slice(0, 2).map((tag, idx) => (
          <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
            {tag}
          </span>
        ))}
      </div>

      {/* Info */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <div className="flex items-center gap-1">
          <BarChart3 className="h-4 w-4" />
          <span>{topic.difficulty}</span>
        </div>
        <div className="flex items-center gap-1">
          <Users className="h-4 w-4" />
          <span>{topic.personas.length} experts</span>
        </div>
      </div>

      {/* CTA */}
      <button 
        onClick={() => onSelect(topic)}
        className="w-full flex items-center justify-between pt-3 border-t border-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
        aria-label={`Start ${topic.title} workshop`}
      >
        <span className="text-sm font-semibold text-gray-600 group-hover:text-blue-600 transition-colors">
          Start Workshop
        </span>
        <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
      </button>
    </article>
  );
};

// Beautiful, simple custom workshop form
const CustomTopicInput = ({ onSubmit, isLoading }) => {
  const [formData, setFormData] = useState({
    challenge: '',
    users: '',
    context: ''
  });
  
  const [showExamples, setShowExamples] = useState(false);
  
  const examples = [
    {
      challenge: "Redesign our mobile banking app to be more user-friendly",
      users: "Young professionals who bank on their phones",
      context: "Financial startup with frustrated users"
    },
    {
      challenge: "Create an accessible fitness app for older adults",
      users: "Active seniors who want to stay healthy",
      context: "Healthcare company focused on wellness"
    },
    {
      challenge: "Improve our e-commerce checkout experience",
      users: "Online shoppers who abandon their carts",
      context: "Retail website with conversion issues"
    }
  ];

  const handleSubmit = () => {
    const fullChallenge = `Challenge: ${formData.challenge}\n\nTarget Users: ${formData.users}\n\nContext: ${formData.context}`;
    onSubmit(fullChallenge);
  };

  const handleExampleClick = (example) => {
    setFormData(example);
    setShowExamples(false);
  };

  const isFormValid = () => {
    return formData.challenge.trim() && formData.users.trim();
  };
  
  return (
    <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-3xl border border-purple-200 p-6 sm:p-8 shadow-lg">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-indigo-600 rounded-3xl flex items-center justify-center mx-auto mb-4 shadow-xl">
          <Edit3 className="h-8 w-8 text-white" />
        </div>
        <h3 className="font-bold text-gray-900 text-2xl sm:text-3xl mb-3">Create Your Workshop</h3>
        <p className="text-gray-600 text-lg">Build a custom design challenge</p>
      </div>

      {/* Examples Section */}
      <div className="mb-8">
        <button
          onClick={() => setShowExamples(!showExamples)}
          className="w-full bg-white hover:bg-purple-50 border-2 border-purple-200 hover:border-purple-300 rounded-2xl p-4 transition-all duration-200 group focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
          aria-label="Toggle example workshops"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                <Lightbulb className="h-5 w-5 text-purple-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-gray-900">Need inspiration?</div>
                <div className="text-sm text-gray-600">See example workshops</div>
              </div>
            </div>
            <ChevronRight className={`h-5 w-5 text-purple-600 transition-transform duration-200 ${showExamples ? 'rotate-90' : ''}`} />
          </div>
        </button>

        {showExamples && (
          <div className="mt-4 space-y-3">
            {examples.map((example, idx) => (
              <button
                key={idx}
                onClick={() => handleExampleClick(example)}
                className="w-full text-left bg-white hover:bg-purple-50 border border-purple-200 hover:border-purple-300 rounded-xl p-4 transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                aria-label={`Use example: ${example.challenge}`}
              >
                <div className="font-medium text-gray-900 mb-2">{example.challenge}</div>
                <div className="text-sm text-gray-600 mb-1">👥 {example.users}</div>
                <div className="text-sm text-gray-500">🏢 {example.context}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Form Fields */}
      <div className="space-y-6">
        {/* Challenge Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center">
              <Target className="h-4 w-4 text-red-600" />
            </div>
            <label htmlFor="challenge-input" className="font-bold text-gray-900 text-lg">
              What's your design challenge?
            </label>
          </div>
          <div className="relative">
            <textarea
              id="challenge-input"
              value={formData.challenge}
              onChange={(e) => setFormData(prev => ({...prev, challenge: e.target.value}))}
              placeholder="Tell us what you want to improve or create. Be as specific or general as you like..."
              className="w-full p-4 sm:p-5 bg-white border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 rounded-2xl outline-none text-gray-800 text-base sm:text-lg leading-relaxed resize-none transition-all duration-200 min-h-[120px] sm:min-h-[100px]"
              rows={4}
            />
            {formData.challenge && (
              <div className="absolute top-3 right-3">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
            )}
          </div>
        </div>

        {/* Users Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="h-4 w-4 text-blue-600" />
            </div>
            <label htmlFor="users-input" className="font-bold text-gray-900 text-lg">
              Who are you designing for?
            </label>
          </div>
          <div className="relative">
            <input
              type="text"
              id="users-input"
              value={formData.users}
              onChange={(e) => setFormData(prev => ({...prev, users: e.target.value}))}
              placeholder="Describe your users in your own words..."
              className="w-full p-4 sm:p-5 bg-white border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 rounded-2xl outline-none text-gray-800 text-base sm:text-lg transition-all duration-200"
            />
            {formData.users && (
              <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
            )}
          </div>
        </div>

        {/* Context Field */}
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <Globe className="h-4 w-4 text-green-600" />
            </div>
            <label htmlFor="context-input" className="font-bold text-gray-900 text-lg">
              Any background context? <span className="text-gray-500 font-normal">(optional)</span>
            </label>
          </div>
          <div className="relative">
            <input
              type="text"
              id="context-input"
              value={formData.context}
              onChange={(e) => setFormData(prev => ({...prev, context: e.target.value}))}
              placeholder="Company type, industry, current situation... (optional)"
              className="w-full p-4 sm:p-5 bg-white border-2 border-gray-200 hover:border-purple-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-100 rounded-2xl outline-none text-gray-800 text-base sm:text-lg transition-all duration-200"
            />
            {formData.context && (
              <div className="absolute top-1/2 right-4 transform -translate-y-1/2">
                <CheckCircle className="h-5 w-5 text-green-500" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8">
        <Button
          onClick={handleSubmit}
          disabled={!isFormValid()}
          isLoading={isLoading}
          variant="primary"
          icon={<Sparkles className="h-5 w-5" />}
          size="lg"
          fullWidth={true}
          className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-200"
        >
          {isLoading ? 'Creating Your Workshop...' : 'Create My Workshop'}
        </Button>
      </div>

      {/* Help Text */}
      <div className="mt-6 text-center">
        <div className="inline-flex items-center gap-2 bg-white bg-opacity-60 rounded-full px-4 py-2">
          <BrainCircuit className="h-4 w-4 text-purple-600" />
          <span className="text-sm text-gray-600">
            We'll create the right expert team for your challenge
          </span>
        </div>
      </div>
    </div>
  );
};

// Mobile-optimized persona card
const PersonaCard = ({ persona, onRemove, canRemove = false }) => {
  return (
    <div className="group relative">
      <div className={`${persona.bgColor} rounded-xl p-3 sm:p-4 text-center hover:shadow-md transition-all duration-200 border border-gray-200 min-h-[120px] sm:min-h-[140px]`}>
        <div className="relative">
          <div className="w-10 h-10 sm:w-14 sm:h-14 mx-auto mb-2 sm:mb-3 text-lg sm:text-2xl flex items-center justify-center rounded-full bg-white shadow-sm">
            {persona.avatar}
          </div>
          {canRemove && (
            <button
              onClick={() => onRemove(persona)}
              className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 touch-manipulation focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              aria-label={`Remove ${persona.name} from team`}
            >
              <X className="h-2 w-2 sm:h-3 sm:w-3" />
            </button>
          )}
        </div>
        <div className="font-bold text-gray-900 text-xs sm:text-sm mb-1 truncate">{persona.name}</div>
        <div className="text-xs text-gray-600 mb-2 truncate leading-tight">{persona.role}</div>
        <div className="text-xs bg-white text-gray-700 px-2 py-1 rounded-full font-medium truncate">
          {persona.specialty || persona.perspective}
        </div>
        {persona.isCustom && (
          <div className="text-xs text-blue-600 font-bold mt-2 flex items-center justify-center gap-1">
            <Star className="h-2 w-2 sm:h-3 sm:w-3" />
            Custom
          </div>
        )}
      </div>
    </div>
  );
};

// Mobile-optimized team display
const TeamDisplay = ({ personas, onAddPersona, onRemovePersona, isLoading }) => {
  const [showAddForm, setShowAddForm] = useState(false);

  return (
    <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 mb-6 sm:mb-8 shadow-sm">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h3 className="font-bold text-gray-900 text-lg sm:text-xl flex items-center gap-2 sm:gap-3">
          <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <Users className="h-3 w-3 sm:h-5 sm:w-5 text-white" />
          </div>
          <span className="truncate">Team ({personas.length})</span>
        </h3>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          variant="outline"
          size="sm"
          icon={showAddForm ? <X className="h-3 w-3 sm:h-4 sm:w-4" /> : <UserPlus className="h-3 w-3 sm:h-4 sm:w-4" />}
          className="flex-shrink-0"
        >
          <span className="hidden sm:inline">{showAddForm ? 'Cancel' : 'Add Expert'}</span>
          <span className="sm:hidden">{showAddForm ? 'Cancel' : 'Add'}</span>
        </Button>
      </div>

      {/* Add Persona Form */}
      {showAddForm && (
        <AddPersonaForm 
          onAdd={(persona) => {
            onAddPersona(persona);
            setShowAddForm(false);
          }}
          onCancel={() => setShowAddForm(false)}
          isLoading={isLoading}
        />
      )}

      {/* Team Grid - Mobile optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4 mb-4 sm:mb-6">
        {personas.map((persona, idx) => (
          <PersonaCard 
            key={idx} 
            persona={persona} 
            onRemove={onRemovePersona}
            canRemove={persona.isCustom}
          />
        ))}
      </div>

      {/* Team Stats */}
      <div className="bg-blue-50 rounded-xl p-3 sm:p-4">
        <h4 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
          <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4" />
          Team Overview
        </h4>
        <div className="grid grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
          <div className="text-center">
            <div className="font-bold text-lg sm:text-2xl text-blue-600">{personas.length}</div>
            <div className="text-gray-600">Experts</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-lg sm:text-2xl text-green-600">{new Set(personas.map(p => p.role.split(' ')[0])).size}</div>
            <div className="text-gray-600">Disciplines</div>
          </div>
          <div className="text-center">
            <div className="font-bold text-lg sm:text-2xl text-purple-600">{personas.filter(p => p.isCustom).length}</div>
            <div className="text-gray-600">Custom</div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Mobile-optimized add persona form
const AddPersonaForm = ({ onAdd, onCancel, isLoading }) => {
  const [formData, setFormData] = useState({
    name: '',
    role: '',
    perspective: '',
    specialty: ''
  });

  const predefinedRoles = [
    { role: 'UX Researcher', avatar: '👩‍💻', specialty: 'User insights' },
    { role: 'Data Analyst', avatar: '👨‍💼', specialty: 'Metrics & KPIs' },
    { role: 'Product Manager', avatar: '👩‍💼', specialty: 'Strategy' },
    { role: 'Engineer', avatar: '👨‍💻', specialty: 'Technical feasibility' },
    { role: 'Designer', avatar: '👩‍🎨', specialty: 'Visual design' },
    { role: 'Customer Success', avatar: '👨‍💼', specialty: 'User advocacy' }
  ];

  const handleQuickAdd = (roleData) => {
    setFormData(prev => ({
      ...prev,
      role: roleData.role,
      specialty: roleData.specialty
    }));
  };

  const handleSubmit = () => {
    if (formData.name && formData.role && formData.perspective) {
      const newPersona = {
        name: formData.name,
        role: formData.role,
        avatar: predefinedRoles.find(r => r.role === formData.role)?.avatar || '👤',
        bgColor: 'bg-gray-100',
        perspective: formData.perspective,
        specialty: formData.specialty || formData.perspective,
        isCustom: true
      };
      onAdd(newPersona);
    }
  };

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 sm:p-6 mb-4 sm:mb-6">
      <h4 className="font-bold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Add Team Expert</h4>
      
      {/* Quick Role Selection - Mobile optimized */}
      <div className="mb-3 sm:mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Quick Role Selection</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {predefinedRoles.map((roleData, idx) => (
            <button
              key={idx}
              onClick={() => handleQuickAdd(roleData)}
              className={`p-2 sm:p-3 text-xs sm:text-sm rounded-lg border-2 transition-all min-h-[60px] sm:min-h-[70px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                formData.role === roleData.role 
                  ? 'border-blue-500 bg-blue-100 text-blue-700' 
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
              aria-label={`Select ${roleData.role} role`}
            >
              <div className="text-base sm:text-lg mb-1">{roleData.avatar}</div>
              <div className="font-medium leading-tight">{roleData.role}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Form Fields */}
      <div className="space-y-3 sm:space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label htmlFor="expert-name" className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
            <input
              type="text"
              id="expert-name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({...prev, name: e.target.value}))}
              placeholder="e.g., Alex"
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm sm:text-base min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="expert-role" className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
            <input
              type="text"
              id="expert-role"
              value={formData.role}
              onChange={(e) => setFormData(prev => ({...prev, role: e.target.value}))}
              placeholder="e.g., Senior UX Researcher"
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm sm:text-base min-h-[44px]"
            />
          </div>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label htmlFor="expert-perspective" className="block text-sm font-semibold text-gray-700 mb-2">Perspective</label>
            <input
              type="text"
              id="expert-perspective"
              value={formData.perspective}
              onChange={(e) => setFormData(prev => ({...prev, perspective: e.target.value}))}
              placeholder="e.g., Data-driven insights"
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm sm:text-base min-h-[44px]"
            />
          </div>
          <div>
            <label htmlFor="expert-specialty" className="block text-sm font-semibold text-gray-700 mb-2">Specialty</label>
            <input
              type="text"
              id="expert-specialty"
              value={formData.specialty}
              onChange={(e) => setFormData(prev => ({...prev, specialty: e.target.value}))}
              placeholder="e.g., Behavioral analysis"
              className="w-full p-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none transition-all text-sm sm:text-base min-h-[44px]"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 sm:gap-3 mt-4 sm:mt-6">
        <Button
          onClick={handleSubmit}
          disabled={!formData.name || !formData.role || !formData.perspective}
          variant="success"
          className="flex-1"
          icon={<Plus className="h-3 w-3 sm:h-4 sm:w-4" />}
          size="sm"
        >
          Add Expert
        </Button>
        <Button
          onClick={onCancel}
          variant="ghost"
          className="flex-1"
          size="sm"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
};

// Mobile-optimized insights display
const InsightCard = ({ insight, persona }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <article className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 hover:shadow-lg transition-shadow">
      {/* Persona Header */}
      <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
        <div className={`w-10 h-10 sm:w-12 sm:h-12 ${persona?.bgColor || 'bg-gray-100'} rounded-xl flex items-center justify-center text-base sm:text-xl shadow-sm flex-shrink-0`}>
          {persona?.avatar || '👤'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
            <span className="font-bold text-gray-900 truncate text-sm sm:text-base">{insight.persona}</span>
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium self-start">
              {persona?.specialty || insight.perspective}
            </span>
          </div>
          <p className="text-xs sm:text-sm text-gray-600">{insight.perspective}</p>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-3 sm:space-y-4">
        {insight.content.slice(0, isExpanded ? insight.content.length : 2).map((item, itemIdx) => (
          <div key={itemIdx} className="bg-gray-50 rounded-xl p-3 sm:p-4">
            {item.title && (
              <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2 text-sm sm:text-base">
                <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-blue-500 rounded-full"></div>
                {item.title}
              </h5>
            )}
            <p className="text-gray-700 mb-2 sm:mb-3 leading-relaxed text-sm sm:text-base">{item.description}</p>
            {item.insights && (
              <ul className="space-y-1 sm:space-y-2">
                {item.insights.map((point, pointIdx) => (
                  <li key={pointIdx} className="text-xs sm:text-sm text-gray-600 flex items-start gap-2 sm:gap-3">
                    <div className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-blue-400 rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                    <span className="leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {/* Expand/Collapse */}
      {insight.content.length > 2 && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-3 sm:mt-4 flex items-center gap-2 text-xs sm:text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors min-h-[32px] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
          aria-label={isExpanded ? 'Show less content' : `Show ${insight.content.length - 2} more insights`}
        >
          {isExpanded ? <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" /> : <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />}
          {isExpanded ? 'Show Less' : `Show ${insight.content.length - 2} More Insights`}
        </button>
      )}
    </article>
  );
};

// Mobile-optimized stage component
const InteractiveStageComponent = ({ 
  stage, 
  isActive, 
  isCompleted, 
  insights, 
  onGenerate, 
  isLoading, 
  challenge, 
  personas,
  onAddUserInput,
  userInsights = []
}) => {
  const StageIcon = stage.icon;
  const [input, setInput] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  const colors = {
    rose: { bg: 'bg-rose-50', text: 'text-rose-700', border: 'border-rose-200', accent: 'bg-rose-500' },
    blue: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', accent: 'bg-blue-500' },
    amber: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', accent: 'bg-amber-500' },
    green: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', accent: 'bg-green-500' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200', accent: 'bg-purple-500' }
  };
  const color = colors[stage.color];
  
  if (!isActive && !isCompleted) {
    return (
      <div className="bg-gray-100 rounded-2xl border border-gray-200 p-4 sm:p-8 opacity-50">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gray-300 rounded-xl flex items-center justify-center">
            <StageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-gray-500" />
          </div>
          <div>
            <h3 className="text-lg sm:text-2xl font-bold text-gray-500">{stage.name}</h3>
            <p className="text-sm sm:text-base text-gray-400">Complete previous stages to unlock</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <section className={`bg-white rounded-2xl border-2 shadow-lg transition-shadow ${
      isCompleted ? 'border-green-300 bg-green-50' : color.border
    }`}>
      {/* Header */}
      <header className={`${color.bg} px-4 py-4 sm:px-8 sm:py-6 rounded-t-2xl border-b ${color.border}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className={`w-12 h-12 sm:w-16 sm:h-16 ${color.accent} rounded-xl flex items-center justify-center shadow-lg`}>
              <StageIcon className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
            </div>
            <div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-1 sm:mb-2">
                <h3 className="text-xl sm:text-3xl font-bold text-gray-900">{stage.name}</h3>
                <span className="text-xs bg-white text-gray-600 px-2 sm:px-3 py-1 rounded-full self-start">
                  Stage {STAGES.findIndex(s => s.id === stage.id) + 1} of {STAGES.length}
                </span>
              </div>
              <p className="text-gray-700 text-sm sm:text-lg">{stage.description}</p>
            </div>
          </div>
          {isCompleted && (
            <div className="flex items-center gap-1 sm:gap-2 bg-green-100 rounded-xl px-2 py-1 sm:px-4 sm:py-2 ml-2">
              <CheckCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
              <span className="text-green-700 font-semibold text-xs sm:text-base hidden sm:inline">Complete</span>
            </div>
          )}
        </div>
      </header>
      
      <div className="p-4 sm:p-8">
        {/* Compact Stage Focus */}
        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 sm:p-5 mb-6">
          <h4 className="font-bold text-gray-900 text-base sm:text-lg mb-2 flex items-center gap-2">
            <Target className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
            {stage.prompt}
          </h4>
          <p className="text-gray-600 text-sm leading-relaxed mb-3">{stage.focus}</p>
          <div className="flex flex-wrap gap-2">
            {stage.outcomes.slice(0, 2).map((outcome, idx) => (
              <span key={idx} className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                {outcome}
              </span>
            ))}
          </div>
        </div>
        
        {/* Grid Layout for Input and Generate */}
        {isActive && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* User Input - Smaller */}
            <div className="lg:col-span-2">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-4">
                <h4 className="font-bold text-blue-900 text-sm sm:text-base mb-3 flex items-center gap-2">
                  <MessageCircle className="h-4 w-4 text-blue-600" />
                  Add Your Input
                </h4>
                <label htmlFor={`stage-input-${stage.id}`} className="sr-only">
                  Share your thoughts for {stage.name}
                </label>
                <textarea
                  id={`stage-input-${stage.id}`}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={`Share constraints or priorities for ${stage.name}...`}
                  className="w-full p-3 bg-white border-2 border-blue-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl outline-none resize-none text-sm leading-relaxed transition-all duration-200 min-h-[80px]"
                  rows={3}
                />
                <div className="flex justify-between items-center mt-3">
                  <button
                    onClick={() => setShowSuggestions(!showSuggestions)}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg"
                    aria-label="Toggle suggestion examples"
                  >
                    <Lightbulb className="h-3 w-3" />
                    {showSuggestions ? 'Hide Ideas' : 'Need Ideas?'}
                  </button>
                  <Button
                    onClick={() => {
                      if (input.trim()) {
                        onAddUserInput(input.trim());
                        setInput('');
                      }
                    }}
                    disabled={!input.trim()}
                    variant="success"
                    size="sm"
                    icon={<Send className="h-3 w-3" />}
                  >
                    Add
                  </Button>
                </div>
              </div>
              
              {/* Compact Suggestions */}
              {showSuggestions && (
                <div className="mt-4 bg-white border border-blue-200 rounded-xl p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      "Focus on mobile users",
                      "Budget under $50k", 
                      "Must be accessible",
                      "Quick timeline"
                    ].map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setInput(suggestion);
                          setShowSuggestions(false);
                        }}
                        className="text-left p-2 text-xs bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        aria-label={`Use suggestion: ${suggestion}`}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Previous Inputs */}
              {userInsights.length > 0 && (
                <div className="mt-4">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Your Inputs ({userInsights.length}):</h5>
                  <div className="space-y-2 max-h-24 overflow-y-auto">
                    {userInsights.map((insight, idx) => (
                      <div key={idx} className="bg-white border border-blue-200 rounded-lg p-2">
                        <p className="text-xs text-gray-700">{insight}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Prominent Generate Button Area */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200 rounded-2xl p-6 text-center h-full flex flex-col justify-center min-h-[200px]">
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <BrainCircuit className="h-8 w-8 text-white" />
                </div>
                <h4 className="text-lg font-bold text-gray-900 mb-2">Ready for Expert Insights?</h4>
                <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                  {userInsights.length > 0 
                    ? `${userInsights.length} input${userInsights.length > 1 ? 's' : ''} will guide the team` 
                    : "Get expert perspectives on this stage"
                  }
                </p>
                <Button
                  onClick={onGenerate}
                  isLoading={isLoading}
                  variant="primary"
                  size="lg"
                  icon={<Sparkles className="h-5 w-5" />}
                  fullWidth={true}
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 shadow-xl"
                >
                  {isLoading ? 'Experts Working...' : `Generate ${stage.name} Insights`}
                </Button>
              </div>
            </div>
          </div>
        )}
        
        {/* Insights Display */}
        {insights && (
          <div className="space-y-4 sm:space-y-6">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-gray-900 text-base sm:text-xl flex items-center gap-2">
                <BrainCircuit className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
                <span className="truncate">Expert Insights</span>
              </h4>
              <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                {insights.length} Expert{insights.length > 1 ? 's' : ''}
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
              {insights.map((insight, idx) => {
                const persona = personas.find(p => p.name === insight.persona);
                return (
                  <InsightCard 
                    key={idx} 
                    insight={insight} 
                    persona={persona}
                  />
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Mobile-optimized loading overlay
const LoadingOverlay = ({ message }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-xs sm:max-w-sm mx-auto text-center shadow-xl">
        <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4 relative">
          <div className="w-full h-full border-4 border-gray-200 rounded-full"></div>
          <div className="absolute top-0 left-0 w-full h-full border-4 border-blue-500 rounded-full animate-spin border-t-transparent"></div>
        </div>
        <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2">{message}</h3>
        <p className="text-gray-600 text-sm sm:text-base">Our experts are collaborating...</p>
      </div>
    </div>
  );
};

// Main enhanced component
const EnhancedDesignThinkingWorkshop = () => {
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [personas, setPersonas] = useState([]);
  const [currentStageIndex, setCurrentStageIndex] = useState(0);
  const [completedStages, setCompletedStages] = useState([]);
  const [stageInsights, setStageInsights] = useState({});
  const [userStageInputs, setUserStageInputs] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [toast, setToast] = useState(null);
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  
  const startWorkshop = (topic) => {
    setSelectedTopic(topic);
    setPersonas([...topic.personas]);
    setCurrentStageIndex(0);
    setCompletedStages([]);
    setStageInsights({});
    setUserStageInputs({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setToast({ message: `${topic.title} workshop started!`, type: 'success' });
  };
  
  const createCustomWorkshop = async (customChallenge) => {
    setIsCreatingCustom(true);
    setIsLoading(true);
    setLoadingMessage('Creating Your Custom Expert Team');
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 3000,
          temperature: 0.7,
          messages: [{
            role: 'user',
            content: `Create a custom design thinking workshop for this challenge: "${customChallenge}"

Generate:
1. A compelling title (4-8 words)
2. A brief description (one engaging sentence)
3. 3-5 diverse personas with complementary expertise relevant to this challenge

Each persona should have:
- Name: First name only, diverse and realistic
- Role: Specific job title relevant to the challenge  
- Avatar: Single appropriate emoji
- BgColor: One of: bg-blue-100, bg-green-100, bg-purple-100, bg-pink-100, bg-orange-100, bg-gray-100
- Perspective: Their unique approach (2-3 words)
- Specialty: Their key expertise area (2-4 words)

Respond in JSON format:
{
  "title": "Workshop Title",
  "description": "Brief compelling description",
  "challenge": "Refined version of the challenge with more context",
  "personas": [
    {
      "name": "Sarah",
      "role": "UX Research Lead",
      "avatar": "👩‍💻",
      "bgColor": "bg-blue-100",
      "perspective": "User-centered design",
      "specialty": "Behavioral insights"
    }
  ]
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to create workshop');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const parsedData = JSON.parse(jsonMatch[0]);
        
        const customTopic = {
          id: 'custom-' + Date.now(),
          title: parsedData.title,
          description: parsedData.description,
          icon: <BrainCircuit className="h-5 w-5 sm:h-6 sm:w-6" />,
          gradient: 'from-purple-600 to-indigo-600',
          category: 'Custom',
          difficulty: 'Tailored',
          tags: ['Custom', 'Personalized', 'Your Challenge'],
          challenge: parsedData.challenge || customChallenge,
          metrics: ['Custom outcomes', 'Tailored results', 'Unique insights'],
          personas: parsedData.personas || []
        };
        
        setToast({ message: 'Custom workshop created successfully!', type: 'success' });
        startWorkshop(customTopic);
      } else {
        throw new Error('Could not parse response');
      }
    } catch (error) {
      console.error('Error:', error);
      setToast({ message: 'Workshop creation failed. Please try again.', type: 'error' });
    }
    
    setIsLoading(false);
    setIsCreatingCustom(false);
  };
  
  const addPersona = (persona) => {
    setPersonas(prev => [...prev, persona]);
    setToast({ message: `${persona.name} joined your team!`, type: 'success' });
  };

  const removePersona = (personaToRemove) => {
    setPersonas(prev => prev.filter(p => p !== personaToRemove));
    setToast({ message: `${personaToRemove.name} removed from team.`, type: 'info' });
  };
  
  const addUserStageInput = (stageId, input) => {
    setUserStageInputs(prev => ({
      ...prev,
      [stageId]: [...(prev[stageId] || []), input]
    }));
    setToast({ message: 'Your input captured!', type: 'success' });
  };
  
  const generateStageInsights = async (stageIndex) => {
    const stage = STAGES[stageIndex];
    setIsLoading(true);
    setLoadingMessage(`Generating ${stage.name} Insights`);
    
    const userInputsForStage = userStageInputs[stage.id] || [];
    const userInputText = userInputsForStage.length > 0 
      ? `\n\nIMPORTANT - User's specific requirements: ${userInputsForStage.join('; ')}`
      : '';
    
    try {
      const response = await fetch('https://proxyapi-te6hpgqhgq-uc.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          model: 'claude-3-5-sonnet-20241022',
          max_tokens: 4000,
          temperature: 0.8,
          messages: [{
            role: 'user',
            content: `Generate comprehensive ${stage.name} stage insights for: "${selectedTopic.challenge}"

Expert Team: ${personas.map(p => `${p.name} (${p.role}) - ${p.specialty || p.perspective}`).join(', ')}${userInputText}

Stage Focus: ${stage.focus}
Expected Outcomes: ${stage.outcomes.join(', ')}

Generate detailed insights from each team member's unique expertise for the ${stage.name} stage. Each expert should provide practical, actionable insights.

Respond in JSON format:
{
  "insights": [
    {
      "persona": "Expert Name",
      "perspective": "Their unique approach",  
      "content": [
        {
          "title": "Key Insight Title",
          "description": "Detailed explanation addressing user requirements when provided",
          "insights": ["Specific point 1", "Specific point 2", "Specific point 3"]
        },
        {
          "title": "Another Key Insight",
          "description": "Another detailed explanation",
          "insights": ["Additional point 1", "Additional point 2"]
        }
      ]
    }
  ]
}`
          }]
        })
      });

      if (!response.ok) throw new Error('Failed to generate insights');
      
      const data = await response.json();
      const jsonMatch = data.content[0].text.match(/\{[\s\S]*\}/);
      
      if (jsonMatch) {
        const parsedData = JSON.parse(jsonMatch[0]);
        setStageInsights(prev => ({
          ...prev,
          [stage.id]: parsedData.insights || []
        }));
        setCompletedStages(prev => [...prev, stage.id]);
        
        // Auto-advance
        if (stageIndex < STAGES.length - 1) {
          setToast({ message: `${stage.name} complete! Moving to next stage...`, type: 'success' });
          setTimeout(() => {
            setCurrentStageIndex(stageIndex + 1);
            setTimeout(() => {
              const nextStageElement = document.getElementById(`stage-${stageIndex + 1}`);
              if (nextStageElement) {
                nextStageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }, 1500);
        } else {
          setToast({ message: 'Workshop complete! All insights ready.', type: 'success' });
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setToast({ message: 'Insight generation failed. Please try again.', type: 'error' });
    }
    
    setIsLoading(false);
  };
  
  const resetWorkshop = () => {
    setSelectedTopic(null);
    setPersonas([]);
    setCurrentStageIndex(0);
    setCompletedStages([]);
    setStageInsights({});
    setUserStageInputs({});
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setToast({ message: 'Workshop reset. Ready for new challenge!', type: 'info' });
  };

  const exportResults = () => {
    const workshopData = {
      workshop: {
        title: selectedTopic.title,
        description: selectedTopic.description,
        challenge: selectedTopic.challenge,
        category: selectedTopic.category,
        completedAt: new Date().toISOString()
      },
      team: personas,
      insights: stageInsights,
      userInputs: userStageInputs,
      summary: {
        totalStages: STAGES.length,
        completedStages: completedStages.length,
        teamSize: personas.length,
        customInputs: Object.values(userStageInputs).flat().length
      }
    };
    
    const blob = new Blob([JSON.stringify(workshopData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${selectedTopic.title.replace(/\s+/g, '-').toLowerCase()}-workshop-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
    
    setToast({ message: 'Workshop results downloaded!', type: 'success' });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <SEOHead />
      <HeaderNav />
      
      {/* Loading */}
      {isLoading && <LoadingOverlay message={loadingMessage} />}
      
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-700 pt-16 sm:pt-20 pb-12 sm:pb-16 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-12 h-12 sm:w-16 sm:h-16 mx-auto bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
            <Users className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4 leading-tight px-2">
            Design Thinking Workshop Simulator
          </h1>
          <p className="text-base sm:text-xl text-blue-100 max-w-4xl mx-auto leading-relaxed mb-6 sm:mb-8 px-4">
            Practice the complete design thinking process with expert teams. Your input guides personalized insights through all five stages.
          </p>
          
          {!selectedTopic && (
            <div className="flex flex-wrap justify-center gap-2 sm:gap-4 text-xs sm:text-sm px-4">
              <div className="flex items-center gap-1 sm:gap-2 bg-white bg-opacity-10 rounded-full px-2 sm:px-4 py-1 sm:py-2">
                <Zap className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Interactive Process</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 bg-white bg-opacity-10 rounded-full px-2 sm:px-4 py-1 sm:py-2">
                <Users className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>Expert Team</span>
              </div>
              <div className="flex items-center gap-1 sm:gap-2 bg-white bg-opacity-10 rounded-full px-2 sm:px-4 py-1 sm:py-2">
                <Target className="h-3 w-3 sm:h-4 sm:w-4" />
                <span>5-Stage Method</span>
              </div>
            </div>
          )}
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        
        {/* Topic Selection */}
        {!selectedTopic && (
          <div className="space-y-8 sm:space-y-12">
            <div className="text-center mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">Choose Your Design Challenge</h2>
              <p className="text-base sm:text-xl text-gray-600 max-w-3xl mx-auto">Start with a featured workshop or create your own custom challenge</p>
            </div>
            
            {/* Featured Workshops */}
            <section>
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Featured Workshops</h3>
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                  <Award className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Expert curated</span>
                  <span className="sm:hidden">Curated</span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16">
                {WORKSHOP_TOPICS.map((topic) => (
                  <TopicCard key={topic.id} topic={topic} onSelect={startWorkshop} />
                ))}
              </div>
            </section>
            
            {/* Custom Workshop */}
            <section>
              <div className="flex items-center justify-between mb-6 sm:mb-8">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900">Create Custom Workshop</h3>
                <div className="flex items-center gap-1 sm:gap-2 text-xs sm:text-sm text-gray-500">
                  <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span className="hidden sm:inline">Custom workshops</span>
                  <span className="sm:hidden">Custom</span>
                </div>
              </div>
              <CustomTopicInput 
                onSubmit={createCustomWorkshop} 
                isLoading={isCreatingCustom} 
              />
            </section>
          </div>
        )}
        
        {/* Workshop Flow */}
        {selectedTopic && (
          <div className="space-y-6 sm:space-y-8">
            
            {/* Workshop Header */}
            <section className="bg-white rounded-2xl border border-gray-200 p-4 sm:p-6 lg:p-8 shadow-lg">
              <div className="flex flex-col lg:flex-row items-start justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
                <div className="flex-1 w-full">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br ${selectedTopic.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                      {selectedTopic.icon}
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 rounded-full">
                      {selectedTopic.category}
                    </span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2 sm:mb-3">{selectedTopic.title}</h2>
                  <p className="text-base sm:text-lg text-gray-600 mb-3 sm:mb-4">{selectedTopic.description}</p>
                  
                  {/* Workshop Stats */}
                  <div className="flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                    <div className="flex items-center gap-1 sm:gap-2">
                      <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                      <span className="text-gray-600">{selectedTopic.difficulty}</span>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <Users className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                      <span className="text-gray-600">{personas.length} experts</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full lg:w-auto">
                  <Button
                    onClick={exportResults}
                    disabled={completedStages.length === 0}
                    variant="outline"
                    icon={<Download className="h-3 w-3 sm:h-4 sm:w-4" />}
                    size="sm"
                    className="lg:flex-shrink-0"
                  >
                    <span className="hidden sm:inline">Export Results</span>
                    <span className="sm:hidden">Export</span>
                  </Button>
                  <Button
                    onClick={resetWorkshop}
                    variant="secondary"
                    icon={<RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />}
                    size="sm"
                    className="lg:flex-shrink-0"
                  >
                    <span className="hidden sm:inline">New Workshop</span>
                    <span className="sm:hidden">New</span>
                  </Button>
                </div>
              </div>
              
              {/* Challenge */}
              <div className="bg-gray-50 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8">
                <h3 className="font-bold text-gray-900 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                  <Target className="h-4 w-4 sm:h-5 sm:w-5 text-red-600" />
                  Design Challenge
                </h3>
                <p className="text-gray-700 leading-relaxed text-sm sm:text-base">{selectedTopic.challenge}</p>
              </div>
              
              {/* Team Management */}
              <TeamDisplay 
                personas={personas} 
                onAddPersona={addPersona}
                onRemovePersona={removePersona}
                isLoading={isLoading}
              />
              
              {/* Progress */}
              <div className="bg-blue-50 rounded-xl p-4 sm:p-6">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Workshop Progress</h4>
                    <p className="text-gray-600 text-xs sm:text-sm">
                      {completedStages.length} of {STAGES.length} stages completed 
                      ({Math.round((completedStages.length / STAGES.length) * 100)}%)
                    </p>
                  </div>
                  
                  <div className="flex items-center gap-1 sm:gap-3 w-full sm:w-auto overflow-x-auto">
                    {STAGES.map((stage, idx) => {
                      const isCompleted = completedStages.includes(stage.id);
                      const isCurrent = idx === currentStageIndex;
                      
                      return (
                        <div key={stage.id} className="flex flex-col items-center gap-1 sm:gap-2 flex-shrink-0">
                          <div className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-200 ${
                            isCompleted ? 'bg-green-500 shadow-sm' :
                            isCurrent ? 'bg-blue-500 shadow-sm' : 
                            'bg-gray-300'
                          }`} />
                          <span className="text-xs font-medium text-gray-600 hidden sm:block text-center max-w-[60px] leading-tight">
                            {stage.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="mt-3 sm:mt-4 w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                  <div 
                    className="h-full bg-blue-500 transition-all duration-500 rounded-full"
                    style={{ width: `${(completedStages.length / STAGES.length) * 100}%` }}
                  />
                </div>
              </div>
            </section>
            
            {/* Stages */}
            <div className="space-y-6 sm:space-y-8">
              {STAGES.map((stage, idx) => (
                <div key={stage.id} id={`stage-${idx}`}>
                  <InteractiveStageComponent
                    stage={stage}
                    isActive={idx === currentStageIndex}
                    isCompleted={completedStages.includes(stage.id)}
                    insights={stageInsights[stage.id]}
                    onGenerate={() => generateStageInsights(idx)}
                    isLoading={isLoading}
                    challenge={selectedTopic.challenge}
                    personas={personas}
                    onAddUserInput={(input) => addUserStageInput(stage.id, input)}
                    userInsights={userStageInputs[stage.id] || []}
                  />
                </div>
              ))}
            </div>
            
            {/* Completion */}
            {completedStages.length === STAGES.length && (
              <section className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl p-8 sm:p-12 text-white text-center shadow-lg">
                <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-white bg-opacity-20 rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-lg">
                  <Award className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
                </div>
                <h3 className="text-2xl sm:text-4xl font-bold mb-3 sm:mb-4">Workshop Complete! 🎉</h3>
                <p className="text-green-100 mb-6 sm:mb-8 text-base sm:text-xl max-w-2xl mx-auto leading-relaxed">
                  Congratulations! You've successfully completed all 5 design thinking stages for "{selectedTopic.title}". 
                  Your collaborative insights are ready for implementation.
                </p>
                
                {/* Completion Stats */}
                <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8 max-w-sm sm:max-w-lg mx-auto">
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold">{STAGES.length}</div>
                    <div className="text-green-200 text-xs sm:text-base">Stages</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold">{personas.length}</div>
                    <div className="text-green-200 text-xs sm:text-base">Experts</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl sm:text-3xl font-bold">{Object.values(userStageInputs).flat().length}</div>
                    <div className="text-green-200 text-xs sm:text-base">Inputs</div>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 max-w-sm sm:max-w-md mx-auto">
                  <Button
                    onClick={exportResults}
                    variant="outline"
                    className="bg-white bg-opacity-20 border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-30 flex-1"
                    icon={<Download className="h-4 w-4 sm:h-5 sm:w-5" />}
                    size="sm"
                  >
                    Download Results
                  </Button>
                  <Button
                    onClick={resetWorkshop}
                    variant="outline"
                    className="bg-white bg-opacity-20 border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-30 flex-1"
                    icon={<Plus className="h-4 w-4 sm:h-5 sm:w-5" />}
                    size="sm"
                  >
                    New Workshop
                  </Button>
                </div>
              </section>
            )}
          </div>
        )}
      </main>
      
      <FooterLan />
      
      {/* Enhanced mobile styles */}
      <style jsx global>{`
        * { 
          scroll-behavior: smooth; 
          -webkit-tap-highlight-color: transparent;
        }
        
        .touch-manipulation {
          touch-action: manipulation;
        }
        
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        
        /* Mobile optimizations */
        @media (max-width: 768px) {
          button, input, textarea, select { 
            min-height: 44px;
            font-size: 16px; /* Prevents zoom on iOS */
          }
          
          input:focus, textarea:focus {
            transform: none; /* Prevents jump on focus */
          }
          
          /* Better scrolling on mobile */
          .overflow-y-auto {
            -webkit-overflow-scrolling: touch;
          }
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar { 
          width: 4px;
        }
        ::-webkit-scrollbar-track { 
          background: #f1f5f9; 
          border-radius: 2px; 
        }
        ::-webkit-scrollbar-thumb { 
          background: #cbd5e1; 
          border-radius: 2px; 
        }
        ::-webkit-scrollbar-thumb:hover { 
          background: #94a3b8; 
        }
        
        /* Improved focus states for accessibility */
        button:focus-visible, 
        input:focus-visible, 
        textarea:focus-visible {
          outline: 2px solid #3b82f6;
          outline-offset: 2px;
        }
        
        /* Better tap targets */
        @media (pointer: coarse) {
          button {
            min-height: 48px;
            min-width: 48px;
          }
        }
      `}</style>
    </div>
  );
};

export default EnhancedDesignThinkingWorkshop;